import { useState, useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { supabase } from '@/lib/supabase';
import seoAgentRunsService from '@/services/seoAgentRuns';
import seoAgentsService from '@/services/seoAgents';
import { SeoAgent, SeoAgentMode } from '@/types/api/seo';
import SeoAutomationComponent from './SeoAutomation.component';

type Stats = {
  totalRuns: number;
  totalChanges: number;
  totalTokens: number;
  totalCost: number;
  errorRate: number;
};

const SeoAutomationPage = function (): JSX.Element {
  const { project } = useSeoProject();
  const projectId = project?.id ?? '';
  const [isInitializing, setIsInitializing] = useState(false);
  const [runningAgentId, setRunningAgentId] = useState<string | null>(null);

  const {
    data: agents = [],
    isLoading: isLoadingAgents,
    refetch: refetchAgents,
  } = useSeoFetch<SeoAgent[]>(
    ['seo-agents', projectId],
    () => seoAgentsService.getByProject(projectId),
    { enabled: !!projectId }
  );

  const {
    data: stats = null,
    isLoading: isLoadingStats,
    refetch: refetchStats,
  } = useSeoFetch<Stats>(
    ['seo-agent-stats', projectId],
    () => seoAgentRunsService.getRecentStats(projectId),
    { enabled: !!projectId }
  );

  const handleToggleEnabled = useCallback(
    async (agentId: string, enabled: boolean) => {
      await seoAgentsService.toggleEnabled(agentId, enabled);
      refetchAgents();
    },
    [refetchAgents]
  );

  const handleUpdateMode = useCallback(
    async (agentId: string, mode: SeoAgentMode) => {
      await seoAgentsService.updateMode(agentId, mode);
      refetchAgents();
    },
    [refetchAgents]
  );

  const handleRunAgent = useCallback(
    async (agentId: string) => {
      setRunningAgentId(agentId);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await supabase.functions.invoke('seo-agent-runner', {
          body: { action: 'runAgent', agentId, userId: session?.user?.id },
        });
        refetchAgents();
        refetchStats();
      } finally {
        setRunningAgentId(null);
      }
    },
    [refetchAgents, refetchStats]
  );

  const handleInitialize = useCallback(async () => {
    if (!projectId) return;
    setIsInitializing(true);
    try {
      await seoAgentsService.initializeForProject(projectId);
      refetchAgents();
      refetchStats();
    } finally {
      setIsInitializing(false);
    }
  }, [projectId, refetchAgents, refetchStats]);

  return (
    <SeoAutomationComponent
      agents={agents}
      stats={stats}
      isLoading={isLoadingAgents || isLoadingStats}
      onToggleEnabled={handleToggleEnabled}
      onUpdateMode={handleUpdateMode}
      onRunAgent={handleRunAgent}
      onInitialize={handleInitialize}
      isInitializing={isInitializing}
      runningAgentId={runningAgentId}
    />
  );
};

export default SeoAutomationPage;
